import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Logo } from "@/components/Logo";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/skills", label: "Skills" },
  { to: "/projects", label: "Projects" },
  { to: "/blog", label: "Blog" },
  { to: "/resume", label: "Resume" },
  { href: "https://sandeepvarikuppala.in?ref=portfolio", label: "Services" },
  { to: "/contact", label: "Contact" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header className="fixed top-0 inset-x-0 z-50 px-4 sm:px-6 pt-4">
      <nav
        data-testid="main-navbar"
        className={`mx-auto max-w-6xl flex items-center justify-between rounded-2xl px-4 sm:px-6 py-3 transition-all duration-300 ${
          scrolled ? "glass shadow-lg" : "bg-transparent"
        }`}
      >
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 font-heading font-semibold text-lg tracking-tight text-foreground">
          {/* Mobile Icon */}
          <img src="/fav/android-chrome-512x512.png" alt="Sandeep Varikuppala Logo Icon" className="h-[3rem] w-[3rem] object-contain sm:hidden" />
          
          {/* Desktop Logo (Inline theme-aware Vector) */}
          <Logo className="hidden sm:block sm:h-[2.8rem]" />
        </Link>

        <div className="hidden md:flex items-center gap-2.5">
          {links.map((l) => {
            if (l.href) {
              return (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/30 transition-all duration-200"
                >
                  {l.label}
                </a>
              );
            }
            return (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? "bg-secondary text-foreground shadow-sm border border-border/40"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/30"
                  }`
                }
              >
                {l.label}
              </NavLink>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Link
            to="/book"
            data-testid="nav-book-cta"
            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity duration-200"
          >
            Book a Call <ArrowUpRight className="w-4 h-4" />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden h-10 w-10 grid place-items-center rounded-full glass"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="md:hidden mx-auto max-w-6xl mt-2 glass rounded-2xl p-3"
            data-testid="mobile-menu"
          >
            {links.map((l) => {
              if (l.href) {
                return (
                  <a
                    key={l.label}
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-4 py-3 rounded-xl text-base text-muted-foreground hover:text-foreground"
                  >
                    {l.label}
                  </a>
                );
              }
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) =>
                    `block px-4 py-3 rounded-xl text-base ${isActive ? "bg-secondary font-medium" : "text-muted-foreground"}`
                  }
                >
                  {l.label}
                </NavLink>
              );
            })}
            <Link to="/book" className="block mt-1 px-4 py-3 rounded-xl bg-primary text-primary-foreground text-center font-medium">
              Book a Call
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
